import { createSelector } from 'reselect';
import { PUBLIC_FEATURE_FLAGS_RECEIVED } from '../actions/constants/ActionTypes';
import * as RequestStatus from '../actions/constants/RequestStatus';
import { actionsDispatchedSelector } from './ActionsDispatchedSelectors';
import { isAppAuthSelector } from './AuthSelectors';
import { numContactsInCrmSelector } from './ContactsSelectors';
import { marketingContactsOnRenewalSelector } from './MarketingContactsSelectors';
import { fetchProductInventoryStatus } from './ProductInventorySelectors';
import { fetchProductsStatus } from './ProductsSelectors';
import { fetchPromotionsStatus } from './PromotionSelectors';
import { fetchPublicMetaDataStatus } from './PublicFeatureFlagSelectors';
import { fetchPurchaseMotionStatus } from './PurchaseMotionSelectors';
import { fetchRequiredOnboardingStatus } from './RequiredOnboardingSelectors';
const numContactsSucceededCheck = state => {
  const numContactsInCrm = numContactsInCrmSelector(state);
  return numContactsInCrm !== null;
};
const marketingContactsOnRenewalSucceededCheck = state => {
  const isInApp = isAppAuthSelector(state);
  if (!isInApp) {
    return true;
  }
  const marketingContactsOnRenewal = marketingContactsOnRenewalSelector(state);
  return marketingContactsOnRenewal !== null && marketingContactsOnRenewal !== undefined;
};

// current required endpoint successes for page load success:
// products, promotions, required onboarding
// & numContactsInCrm, but only if on Marketing Hub Page
export const hasPricingAndPurchasingSucceededSelector = state => {
  const {
    promotions
  } = state;
  const isInApp = isAppAuthSelector(state);
  const requiredOnboardingStatus = fetchRequiredOnboardingStatus(state);
  const productsStatus = fetchProductsStatus(state);
  const ownershipStatus = fetchProductInventoryStatus(state);
  const ownershipComplete = ownershipStatus === RequestStatus.SUCCEEDED || ownershipStatus === RequestStatus.FAILED || !isInApp;
  return productsStatus === RequestStatus.SUCCEEDED && ownershipComplete && !!promotions.productsEligibleForDirectPromotions && numContactsSucceededCheck(state) && marketingContactsOnRenewalSucceededCheck(state) && requiredOnboardingStatus === RequestStatus.SUCCEEDED;
};

// This selector replicates what the above selector does, but with the addition of
// checking if the purchase options are loading. This is because we are migrating the state
// management system from redux to global context. We will do this iteratively, and can only
// remove these selector once all API calls are moved to the global context.
export const hasPricingAndPurchasingSucceededSelectorV2 = (state, {
  isPurchaseOptionsLoading
}) => {
  const {
    promotions
  } = state;
  const isInApp = isAppAuthSelector(state);
  const requiredOnboardingStatus = fetchRequiredOnboardingStatus(state);
  const productsStatus = fetchProductsStatus(state);
  const ownershipStatus = fetchProductInventoryStatus(state);
  const ownershipComplete = ownershipStatus === RequestStatus.SUCCEEDED || ownershipStatus === RequestStatus.FAILED || !isInApp;
  return productsStatus === RequestStatus.SUCCEEDED && ownershipComplete && !!promotions.productsEligibleForDirectPromotions && numContactsSucceededCheck(state) && marketingContactsOnRenewalSucceededCheck(state) && requiredOnboardingStatus === RequestStatus.SUCCEEDED && !isPurchaseOptionsLoading;
};
export const isAppReadySelector = ({
  isPurchaseOptionsLoading
}) => createSelector([state => {
  if (isPurchaseOptionsLoading) {
    return hasPricingAndPurchasingSucceededSelectorV2(state, {
      isPurchaseOptionsLoading
    });
  }
  return hasPricingAndPurchasingSucceededSelector(state);
}], isAppReady => isAppReady);
export const hasPublicMetaDataSucceededSelector = state => {
  const publicRequestStatus = fetchPublicMetaDataStatus(state);
  return publicRequestStatus === RequestStatus.SUCCEEDED;
};
export const hasOnlyOwnershipCheckFailedSelector = state => {
  const productsStatus = fetchProductsStatus(state);
  const ownershipStatus = fetchProductInventoryStatus(state);
  const promotionsStatus = fetchPromotionsStatus(state);
  const requiredOnboardingStatus = fetchRequiredOnboardingStatus(state);
  return ownershipStatus === RequestStatus.FAILED && !(productsStatus === RequestStatus.FAILED || promotionsStatus === RequestStatus.FAILED || !numContactsSucceededCheck(state) || requiredOnboardingStatus === RequestStatus.FAILED);
};
export const hasOwnershipStatusSucceededSelector = state => {
  return fetchProductInventoryStatus(state) === RequestStatus.SUCCEEDED;
};
export const hasProductStatusFailedSelector = state => {
  const productsStatus = fetchProductsStatus(state);
  return productsStatus === RequestStatus.FAILED;
};
export const hasOwnershipStatusFailedSelector = state => {
  const ownershipStatus = fetchProductInventoryStatus(state);
  return ownershipStatus === RequestStatus.FAILED;
};
export const hasPromotionsStatusFailedSelector = state => {
  const promotionsStatus = fetchPromotionsStatus(state);
  return promotionsStatus === RequestStatus.FAILED;
};
export const hasRequiredOnboardingStatusFailedSelector = state => {
  const requiredOnboardingStatus = fetchRequiredOnboardingStatus(state);
  return requiredOnboardingStatus === RequestStatus.FAILED;
};
export const hasNumContactsFailedSelector = state => {
  return !numContactsSucceededCheck(state);
};
export const hasProductsSucceededSelector = state => {
  const productsStatus = fetchProductsStatus(state);
  return productsStatus === RequestStatus.SUCCEEDED;
};
export const canCheckForMarketingContactsOnRenewalSelector = state => {
  return hasProductsSucceededSelector(state) && hasOwnershipStatusSucceededSelector(state);
};
export const hasMarketingContactsOnRenewalFailedSelector = state => {
  return canCheckForMarketingContactsOnRenewalSelector(state) && !marketingContactsOnRenewalSucceededCheck(state);
};
export const hasPurchaseMotionsSucceededSelector = state => {
  const purchaseMotionStatus = fetchPurchaseMotionStatus(state);
  return purchaseMotionStatus === RequestStatus.SUCCEEDED;
};
export const hasPurchaseMotionsFailedSelector = state => {
  const purchaseMotionStatus = fetchPurchaseMotionStatus(state);
  return purchaseMotionStatus === RequestStatus.FAILED;
};
export const hasPromotionsSucceededSelector = state => {
  const promotionsStatus = fetchPromotionsStatus(state);
  return promotionsStatus === RequestStatus.SUCCEEDED;
};
export const hasPricingAndPurchasingFailedSelector = state => {
  return hasProductStatusFailedSelector(state) || hasOwnershipStatusFailedSelector(state) || hasPromotionsStatusFailedSelector(state) || hasNumContactsFailedSelector(state) || hasMarketingContactsOnRenewalFailedSelector(state) || hasRequiredOnboardingStatusFailedSelector(state);
};
export const isMerchCardCTAsDataLoadingSelector = state => {
  const hasPurchaseMotionsSucceeded = hasPurchaseMotionsSucceededSelector(state);
  const hasProductsSucceeded = hasProductsSucceededSelector(state);
  const hasPromotionsSucceeded = hasPromotionsSucceededSelector(state);
  const hasPurchaseMotionsFailed = hasPurchaseMotionsFailedSelector(state);
  return (
    // if purchase motions are not succeeded or failed, then we need to show the loading state
    !hasPurchaseMotionsSucceeded && !hasPurchaseMotionsFailed || !hasProductsSucceeded || !hasPromotionsSucceeded
  );
};
export const hasOfferDataLoadedSelector = state => {
  const hasProductsSucceeded = hasProductsSucceededSelector(state);
  const hasPromotionsSucceeded = hasPromotionsSucceededSelector(state);
  return hasProductsSucceeded && hasPromotionsSucceeded;
};
export const hasGatesLoadedSelector = state => {
  const isAppAuth = isAppAuthSelector(state);
  const actionsDispatched = actionsDispatchedSelector(state);

  // in-app fetches user info, which has the gates, before render
  return actionsDispatched.includes(PUBLIC_FEATURE_FLAGS_RECEIVED) || isAppAuth;
};